import React from 'react';
import { Link } from 'react-router-dom';
import { Heart, FileText, ClipboardList, HelpCircle, Shield, ChevronDown, ChevronUp, MapPin, Phone, Upload, CheckCircle, AlertCircle, X, Loader2, Info, DollarSign } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const faqData = [
  {
    q: 'Will I be turned away if I can\'t pay?',
    a: 'No. No patient will be denied access to medically necessary services due to an inability to pay. Patients experiencing financial hardship are encouraged to speak with our billing staff.',
  },
  {
    q: 'Does this program cover labs or prescriptions?',
    a: 'The sliding fee discount applies to office visit charges. We also work with laboratories, pharmacies, and assistance programs to help patients obtain the lowest available pricing for outside services whenever possible.',
  },
  {
    q: 'How often do I need to renew?',
    a: 'Eligibility is reviewed annually or whenever household size or income changes significantly.',
  },
];

const incomeData = [
  { size: '1', fpl100: '$15,960', fpl150: '$23,940', fpl200: '$31,920' },
  { size: '2', fpl100: '$21,640', fpl150: '$32,460', fpl200: '$43,280' },
  { size: '3', fpl100: '$27,320', fpl150: '$40,980', fpl200: '$54,640' },
  { size: '4', fpl100: '$33,000', fpl150: '$49,500', fpl200: '$66,000' },
  { size: '5', fpl100: '$38,680', fpl150: '$58,020', fpl200: '$77,360' },
  { size: '6', fpl100: '$44,360', fpl150: '$66,540', fpl200: '$88,720' },
  { size: '7', fpl100: '$50,040', fpl150: '$75,060', fpl200: '$100,080' },
  { size: '8', fpl100: '$55,720', fpl150: '$83,580', fpl200: '$111,440' },
];

const responsibilityData = [
  { level: '0\u2013100% FPL', discount: '100% discount', fee: '$20 nominal fee per visit' },
  { level: '101\u2013150% FPL', discount: '75% discount', fee: '$25 per visit' },
  { level: '151\u2013200% FPL', discount: '50% discount', fee: '$50 per visit' },
  { level: 'Above 200% FPL', discount: 'No discount', fee: '$100 (full fee)' },
];

const FAQItem = ({ item, index }) => {
  const [open, setOpen] = React.useState(false);
  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        data-testid={`faq-toggle-${index}`}
        className="w-full flex items-center justify-between px-6 py-5 text-left bg-white hover:bg-gray-50 transition-colors"
      >
        <span className="font-semibold text-gray-900 text-lg pr-4">{item.q}</span>
        {open ? <ChevronUp size={22} className="text-blue-600 flex-shrink-0" /> : <ChevronDown size={22} className="text-gray-400 flex-shrink-0" />}
      </button>
      {open && (
        <div className="px-6 pb-5 text-gray-700 leading-relaxed">
          {item.a}
        </div>
      )}
    </div>
  );
};

const UploadForm = ({ onClose }) => {
  const [formData, setFormData] = React.useState({
    fullName: '',
    dob: '',
    phone: '',
    email: '',
    location: '',
    consent: false,
  });
  const [applicationFile, setApplicationFile] = React.useState(null);
  const [supportingFiles, setSupportingFiles] = React.useState([]);
  const [errors, setErrors] = React.useState({});
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [result, setResult] = React.useState(null);

  const validateFile = (file) => {
    if (!file.name.toLowerCase().endsWith('.pdf')) return 'Only PDF files are accepted.';
    if (file.size > 5 * 1024 * 1024) return 'File must be under 5 MB.';
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};

    if (!formData.fullName.trim()) newErrors.fullName = 'Full name is required.';
    if (!formData.phone.trim()) newErrors.phone = 'Phone number is required.';
    if (!formData.location) newErrors.location = 'Please select a clinic location.';
    if (!formData.consent) newErrors.consent = 'You must agree to proceed.';
    if (!applicationFile) {
      newErrors.applicationFile = 'Please upload your completed application.';
    } else {
      const fileErr = validateFile(applicationFile);
      if (fileErr) newErrors.applicationFile = fileErr;
    }
    for (const sf of supportingFiles) {
      const sfErr = validateFile(sf);
      if (sfErr) { newErrors.supportingFiles = sfErr; break; }
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    setIsSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('fullName', formData.fullName.trim());
      fd.append('phone', formData.phone.trim());
      fd.append('location', formData.location);
      fd.append('consent', 'true');
      if (formData.dob) fd.append('dob', formData.dob);
      if (formData.email.trim()) fd.append('email', formData.email.trim());
      fd.append('applicationFile', applicationFile);
      for (const sf of supportingFiles) {
        fd.append('supportingFiles', sf);
      }

      const resp = await fetch(`${API_URL}/api/sliding-fee/submit`, {
        method: 'POST',
        body: fd,
      });
      const data = await resp.json();

      if (data.success) {
        setResult({ success: true, confirmationId: data.confirmationId });
      } else {
        setResult({ success: false, error: data.error || "We couldn't submit your application. Please call the clinic or try again." });
      }
    } catch {
      setResult({ success: false, error: "We couldn't submit your application. Please call the clinic or try again." });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (result?.success) {
    return (
      <div data-testid="upload-success" className="bg-green-50 border border-green-200 rounded-xl p-8 text-center">
        <CheckCircle size={48} className="text-green-600 mx-auto mb-4" />
        <h3 className="text-2xl font-bold text-gray-900 mb-2">Thank you. Your application has been received.</h3>
        <p className="text-gray-700 mb-1">Your confirmation ID:</p>
        <p data-testid="confirmation-id" className="text-2xl font-mono font-bold text-blue-700 mb-4">{result.confirmationId}</p>
        <p className="text-sm text-gray-500">Please save this ID for your records. Our staff will review your application shortly.</p>
      </div>
    );
  }

  if (result && !result.success) {
    return (
      <div data-testid="upload-error" className="bg-red-50 border border-red-200 rounded-xl p-8 text-center">
        <AlertCircle size={48} className="text-red-600 mx-auto mb-4" />
        <h3 className="text-xl font-bold text-gray-900 mb-2">{result.error}</h3>
        <button
          onClick={() => setResult(null)}
          data-testid="upload-retry-btn"
          className="mt-4 inline-flex items-center gap-2 bg-blue-700 text-white px-6 py-3 rounded-xl font-semibold hover:bg-blue-800 transition-colors"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} data-testid="sfdp-upload-form" className="bg-white border border-gray-200 rounded-xl p-6 lg:p-8 space-y-6">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-xl font-bold text-gray-900">Upload Completed Application</h3>
        <button type="button" onClick={onClose} data-testid="upload-form-close-btn" className="text-gray-400 hover:text-gray-600 transition-colors">
          <X size={24} />
        </button>
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-1">Full Name <span className="text-red-500">*</span></label>
        <input
          type="text"
          data-testid="sfdp-fullname"
          value={formData.fullName}
          onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
          className={`w-full px-4 py-3 rounded-lg border ${errors.fullName ? 'border-red-400' : 'border-gray-300'} focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none`}
          placeholder="Enter your full name"
        />
        {errors.fullName && <p className="text-red-500 text-sm mt-1">{errors.fullName}</p>}
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1">Date of Birth</label>
          <input
            type="date"
            data-testid="sfdp-dob"
            value={formData.dob}
            onChange={(e) => setFormData({ ...formData, dob: e.target.value })}
            className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
          />
        </div>
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1">Phone Number <span className="text-red-500">*</span></label>
          <input
            type="tel"
            data-testid="sfdp-phone"
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            className={`w-full px-4 py-3 rounded-lg border ${errors.phone ? 'border-red-400' : 'border-gray-300'} focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none`}
            placeholder="(386) 555-0100"
          />
          {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1">Email</label>
          <input
            type="email"
            data-testid="sfdp-email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
            placeholder="you@example.com"
          />
        </div>
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1">Preferred Clinic Location <span className="text-red-500">*</span></label>
          <select
            data-testid="sfdp-location"
            value={formData.location}
            onChange={(e) => setFormData({ ...formData, location: e.target.value })}
            className={`w-full px-4 py-3 rounded-lg border ${errors.location ? 'border-red-400' : 'border-gray-300'} focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none bg-white`}
          >
            <option value="">Select location...</option>
            <option value="DeLand">DeLand</option>
            <option value="Orange City">Orange City</option>
          </select>
          {errors.location && <p className="text-red-500 text-sm mt-1">{errors.location}</p>}
        </div>
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-1">Upload Completed Application <span className="text-red-500">*</span></label>
        <div className={`border-2 border-dashed rounded-xl p-6 text-center transition-colors ${errors.applicationFile ? 'border-red-400 bg-red-50' : 'border-gray-300 hover:border-blue-400 bg-gray-50'}`}>
          <input
            type="file"
            accept=".pdf"
            data-testid="sfdp-application-file"
            onChange={(e) => { setApplicationFile(e.target.files[0] || null); setErrors((prev) => ({ ...prev, applicationFile: undefined })); }}
            className="hidden"
            id="app-file-input"
          />
          <label htmlFor="app-file-input" className="cursor-pointer">
            <Upload size={28} className="mx-auto mb-2 text-gray-400" />
            {applicationFile ? (
              <p className="text-blue-700 font-semibold">{applicationFile.name}</p>
            ) : (
              <p className="text-gray-500">Click to select PDF (max 5 MB)</p>
            )}
          </label>
        </div>
        {errors.applicationFile && <p className="text-red-500 text-sm mt-1">{errors.applicationFile}</p>}
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-1">Upload Supporting Documents <span className="text-gray-400 font-normal">(optional, multiple PDFs allowed)</span></label>
        <div className="border-2 border-dashed rounded-xl p-6 text-center border-gray-300 hover:border-blue-400 bg-gray-50 transition-colors">
          <input
            type="file"
            accept=".pdf"
            multiple
            data-testid="sfdp-supporting-files"
            onChange={(e) => { setSupportingFiles(Array.from(e.target.files)); setErrors((prev) => ({ ...prev, supportingFiles: undefined })); }}
            className="hidden"
            id="support-file-input"
          />
          <label htmlFor="support-file-input" className="cursor-pointer">
            <Upload size={28} className="mx-auto mb-2 text-gray-400" />
            {supportingFiles.length > 0 ? (
              <p className="text-blue-700 font-semibold">{supportingFiles.length} file{supportingFiles.length > 1 ? 's' : ''} selected</p>
            ) : (
              <p className="text-gray-500">Click to select PDFs (max 5 MB each)</p>
            )}
          </label>
        </div>
        {errors.supportingFiles && <p className="text-red-500 text-sm mt-1">{errors.supportingFiles}</p>}
      </div>

      <div className={`flex items-start gap-3 p-4 rounded-xl ${errors.consent ? 'bg-red-50 border border-red-200' : 'bg-gray-50'}`}>
        <input
          type="checkbox"
          id="sfdp-consent"
          data-testid="sfdp-consent"
          checked={formData.consent}
          onChange={(e) => setFormData({ ...formData, consent: e.target.checked })}
          className="mt-1 w-5 h-5 text-blue-600 rounded border-gray-300 flex-shrink-0"
        />
        <label htmlFor="sfdp-consent" className="text-sm text-gray-700 leading-relaxed">
          I authorize Community Medical Center of West Volusia to use this application and documentation to determine sliding fee eligibility. <span className="text-red-500">*</span>
        </label>
      </div>
      {errors.consent && <p className="text-red-500 text-sm -mt-4">{errors.consent}</p>}

      <button
        type="submit"
        disabled={isSubmitting}
        data-testid="sfdp-submit-btn"
        className="w-full bg-blue-700 text-white py-4 rounded-xl font-bold text-lg hover:bg-blue-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
      >
        {isSubmitting ? (
          <>
            <Loader2 size={22} className="animate-spin" />
            Submitting...
          </>
        ) : (
          <>
            <Upload size={22} />
            Submit Application
          </>
        )}
      </button>
    </form>
  );
};

const SlidingFee = () => {
  const [showUploadForm, setShowUploadForm] = React.useState(false);

  return (
    <div className="min-h-screen pt-24 lg:pt-28">
      {/* Hero */}
      <section className="relative bg-gradient-to-br from-blue-700 to-teal-600 text-white py-20 lg:py-24 px-4 overflow-hidden">
        <div className="max-w-7xl mx-auto text-center relative z-10">
          <h1 className="text-4xl lg:text-5xl font-extrabold mb-6">Sliding Fee Discount Program</h1>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto">
            Ensuring access to quality healthcare for all patients, regardless of ability to pay.
          </p>
        </div>
      </section>

      {/* Section 1: Introduction */}
      <section className="py-20 lg:py-24 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-4 mb-8">
            <div className="bg-blue-100 text-blue-700 p-3 rounded-xl flex-shrink-0">
              <Heart size={28} />
            </div>
            <h2 className="text-3xl font-extrabold text-gray-900">Our Commitment</h2>
          </div>
          <div className="text-lg text-gray-700 leading-relaxed space-y-4">
            <p>
              Community Medical Center of West Volusia is committed to providing quality healthcare services to all patients, regardless of their ability to pay.
            </p>
            <p>
              We offer a Sliding Fee Discount Program (SFDP) to ensure that eligible patients receive care at a reduced cost based on their household income and family size.
            </p>
          </div>
          <div className="mt-8 bg-blue-50 border-l-4 border-blue-600 rounded-r-xl p-6 flex items-start gap-3">
            <Shield size={22} className="text-blue-600 mt-0.5 flex-shrink-0" />
            <p className="text-gray-800 leading-relaxed font-semibold">
              No patient will be denied access to services due to inability to pay.
            </p>
          </div>
          <div className="mt-4 bg-gray-50 border-l-4 border-gray-400 rounded-r-xl p-6">
            <p className="text-gray-700 leading-relaxed text-sm">
              <strong>Non-Discrimination Statement:</strong> Services are provided to all individuals regardless of race, color, national origin, ethnicity, language, disability, sex, age, sexual orientation, gender identity, religion, or ability to pay.
            </p>
          </div>
        </div>
      </section>

      {/* Section 2: How the Program Works */}
      <section className="py-20 lg:py-24 px-4 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-4 mb-8">
            <div className="bg-teal-100 text-teal-700 p-3 rounded-xl flex-shrink-0">
              <Shield size={28} />
            </div>
            <h2 className="text-3xl font-extrabold text-gray-900">How the Program Works</h2>
          </div>
          <p className="text-lg text-gray-700 leading-relaxed mb-8">
            Discounts are based on the Federal Poverty Guidelines (FPL) and are updated annually.
          </p>
          <div className="bg-white rounded-xl p-6 shadow-md border border-gray-100">
            <h3 className="font-bold text-gray-900 text-lg mb-4">Eligibility is determined using:</h3>
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start gap-3">
                <div className="w-2 h-2 bg-teal-600 rounded-full mt-2 flex-shrink-0"></div>
                <span>Household income</span>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-2 h-2 bg-teal-600 rounded-full mt-2 flex-shrink-0"></div>
                <span>Family size</span>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-2 h-2 bg-teal-600 rounded-full mt-2 flex-shrink-0"></div>
                <span>Supporting documentation (proof of income required)</span>
              </li>
            </ul>
            <p className="mt-6 text-gray-600 text-sm bg-gray-50 rounded-lg p-4">
              Patients must complete a sliding fee application and provide documentation before discounts can be applied.
            </p>
          </div>
        </div>
      </section>

      {/* Section 3: 2026 Income Eligibility Table */}
      <section className="py-20 lg:py-24 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-extrabold text-gray-900 mb-4">2026 Income Eligibility Table</h2>
          <p className="text-lg text-gray-700 mb-8">
            Income thresholds based on household size and the Federal Poverty Guidelines.
          </p>

          {/* Desktop Table */}
          <div className="hidden md:block overflow-x-auto">
            <table data-testid="income-eligibility-table" className="w-full bg-white rounded-xl shadow-md overflow-hidden border border-gray-200">
              <thead>
                <tr className="bg-blue-700 text-white">
                  <th className="px-6 py-4 text-left font-bold">Family Size</th>
                  <th className="px-6 py-4 text-left font-bold">100% FPL</th>
                  <th className="px-6 py-4 text-left font-bold">150% FPL</th>
                  <th className="px-6 py-4 text-left font-bold">200% FPL</th>
                </tr>
              </thead>
              <tbody>
                {incomeData.map((row, i) => (
                  <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="px-6 py-4 font-bold text-gray-900">{row.size}</td>
                    <td className="px-6 py-4 text-gray-700">{row.fpl100}</td>
                    <td className="px-6 py-4 text-gray-700">{row.fpl150}</td>
                    <td className="px-6 py-4 text-gray-700">{row.fpl200}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="md:hidden space-y-4">
            {incomeData.map((row, i) => (
              <div key={i} data-testid={`income-card-${i}`} className="bg-white rounded-xl shadow-md border border-gray-200 p-5">
                <h3 className="font-bold text-blue-700 text-lg mb-3">Family Size: {row.size}</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600 font-medium">100% FPL</span>
                    <span className="text-gray-900 font-semibold">{row.fpl100}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 font-medium">150% FPL</span>
                    <span className="text-gray-900 font-semibold">{row.fpl150}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 font-medium">200% FPL</span>
                    <span className="text-gray-900 font-semibold">{row.fpl200}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <p className="mt-6 text-gray-600 bg-blue-50 border-l-4 border-blue-600 rounded-r-xl p-4">
            For households larger than 8, add <strong>$5,680</strong> for each additional person.
          </p>
        </div>
      </section>

      {/* Section 4: Patient Responsibility Table */}
      <section className="py-20 lg:py-24 px-4 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-4 mb-8">
            <div className="bg-blue-100 text-blue-700 p-3 rounded-xl flex-shrink-0">
              <DollarSign size={28} />
            </div>
            <h2 className="text-3xl font-extrabold text-gray-900">Patient Responsibility</h2>
          </div>
          <p className="text-lg text-gray-700 leading-relaxed mb-8">
            The table below shows the discount level and patient fee based on household income relative to the Federal Poverty Level.
          </p>

          {/* Desktop Table */}
          <div className="hidden md:block overflow-x-auto">
            <table data-testid="responsibility-table" className="w-full bg-white rounded-xl shadow-md overflow-hidden border border-gray-200">
              <thead>
                <tr className="bg-blue-700 text-white">
                  <th className="px-6 py-4 text-left font-bold">Income Level</th>
                  <th className="px-6 py-4 text-left font-bold">Discount</th>
                  <th className="px-6 py-4 text-left font-bold">Patient Fee</th>
                </tr>
              </thead>
              <tbody>
                {responsibilityData.map((row, i) => (
                  <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="px-6 py-4 font-semibold text-gray-900">{row.level}</td>
                    <td className="px-6 py-4 text-gray-700">{row.discount}</td>
                    <td className="px-6 py-4 font-semibold text-gray-900">{row.fee}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="md:hidden space-y-4">
            {responsibilityData.map((row, i) => (
              <div key={i} data-testid={`responsibility-card-${i}`} className="bg-white rounded-xl shadow-md border border-gray-200 p-5">
                <h3 className="font-bold text-blue-700 text-lg mb-2">{row.level}</h3>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Discount</span>
                    <span className="text-gray-900 font-semibold">{row.discount}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Patient Fee</span>
                    <span className="text-gray-900 font-bold">{row.fee}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 5: Additional Information */}
      <section className="py-20 lg:py-24 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-4 mb-8">
            <div className="bg-teal-100 text-teal-700 p-3 rounded-xl flex-shrink-0">
              <Info size={28} />
            </div>
            <h2 className="text-3xl font-extrabold text-gray-900">Additional Information</h2>
          </div>
          <ul className="space-y-4 text-lg text-gray-700">
            <li className="flex items-start gap-4">
              <div className="w-2 h-2 bg-blue-600 rounded-full mt-3 flex-shrink-0"></div>
              <span>Discounts apply to services provided at Community Medical Center of West Volusia.</span>
            </li>
            <li className="flex items-start gap-4">
              <div className="w-2 h-2 bg-blue-600 rounded-full mt-3 flex-shrink-0"></div>
              <span>Sliding fee eligibility must be updated periodically or if financial circumstances change.</span>
            </li>
            <li className="flex items-start gap-4">
              <div className="w-2 h-2 bg-blue-600 rounded-full mt-3 flex-shrink-0"></div>
              <span>Patients with insurance are encouraged to use their coverage; discounts apply to eligible services based on program guidelines.</span>
            </li>
          </ul>
        </div>
      </section>

      {/* How to Apply — PRESERVED EXACTLY */}
      <section className="py-20 lg:py-24 px-4 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-4 mb-8">
            <div className="bg-blue-100 text-blue-700 p-3 rounded-xl flex-shrink-0">
              <ClipboardList size={28} />
            </div>
            <h2 className="text-3xl font-extrabold text-gray-900">How to Apply</h2>
          </div>
          <p className="text-lg text-gray-700 leading-relaxed mb-10">
            Applying is simple and confidential. Patients may apply before their appointment or during their visit
            at either the Orange City or DeLand location.
          </p>

          <div className="space-y-8">
            {/* Step 1 */}
            <div className="flex gap-6">
              <div className="bg-blue-700 text-white w-12 h-12 rounded-full flex items-center justify-center font-bold text-xl flex-shrink-0">1</div>
              <div>
                <h3 className="font-bold text-gray-900 text-xl mb-3">Download the Application</h3>
                <p className="text-gray-700 mb-3">Download and complete the Sliding Fee Discount Program application.</p>
                <div className="flex flex-wrap gap-3">
                  <a
                    href="https://customer-assets.emergentagent.com/job_1d41603b-8f3f-4230-a11f-53915ea71889/artifacts/snhdm7ui_fillable%20CMC_Sliding_Fee_Application.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="download-application-btn"
                    className="inline-flex items-center gap-2 bg-blue-700 text-white px-6 py-3 rounded-xl font-semibold hover:bg-blue-800 transition-colors"
                  >
                    <FileText size={20} />
                    Download Sliding Fee Application (Fillable PDF)
                  </a>
                  <button
                    type="button"
                    onClick={() => setShowUploadForm(!showUploadForm)}
                    data-testid="upload-application-btn"
                    className="inline-flex items-center gap-2 bg-teal-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-teal-700 transition-colors"
                  >
                    <Upload size={20} />
                    Upload Completed Application
                  </button>
                </div>

                {/* Inline Upload Form */}
                {showUploadForm && (
                  <div className="mt-6">
                    <UploadForm onClose={() => setShowUploadForm(false)} />
                  </div>
                )}
              </div>
            </div>

            {/* Step 2 */}
            <div className="flex gap-6">
              <div className="bg-blue-700 text-white w-12 h-12 rounded-full flex items-center justify-center font-bold text-xl flex-shrink-0">2</div>
              <div>
                <h3 className="font-bold text-gray-900 text-xl mb-3">Gather Documentation</h3>
                <p className="text-gray-700 mb-3">To verify eligibility, please provide proof of income for all adults in your household. Acceptable documentation includes:</p>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-blue-600 rounded-full mt-2 flex-shrink-0"></div>
                    <span>Recent pay stubs from the last 30 days</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-blue-600 rounded-full mt-2 flex-shrink-0"></div>
                    <span>Most recent Federal Tax Return or W-2</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-blue-600 rounded-full mt-2 flex-shrink-0"></div>
                    <span>Social Security, Disability, or Unemployment benefit letters</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-blue-600 rounded-full mt-2 flex-shrink-0"></div>
                    <span>If you have no income, a signed letter of support from the individual providing food and shelter</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Step 3 */}
            <div className="flex gap-6">
              <div className="bg-blue-700 text-white w-12 h-12 rounded-full flex items-center justify-center font-bold text-xl flex-shrink-0">3</div>
              <div>
                <h3 className="font-bold text-gray-900 text-xl mb-3">Submit</h3>
                <p className="text-gray-700">
                  Bring the completed application and documentation to the front desk at either location:
                </p>
                <div className="grid sm:grid-cols-2 gap-4 mt-4">
                  <div className="bg-white rounded-xl p-4 flex items-start gap-3 shadow-sm border border-gray-100">
                    <MapPin size={20} className="text-blue-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-gray-900">Orange City Office</p>
                      <p className="text-sm text-gray-600">810 Commed Blvd., Orange City, FL 32763</p>
                    </div>
                  </div>
                  <div className="bg-white rounded-xl p-4 flex items-start gap-3 shadow-sm border border-gray-100">
                    <MapPin size={20} className="text-blue-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-gray-900">DeLand Office</p>
                      <p className="text-sm text-gray-600">1190 N. Stone St., DeLand, FL 32720</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ — PRESERVED EXACTLY */}
      <section className="py-20 lg:py-24 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-4 mb-8">
            <div className="bg-teal-100 text-teal-700 p-3 rounded-xl flex-shrink-0">
              <HelpCircle size={28} />
            </div>
            <h2 className="text-3xl font-extrabold text-gray-900">Frequently Asked Questions</h2>
          </div>
          <div className="space-y-4">
            {faqData.map((item, i) => (
              <FAQItem key={i} item={item} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-20 lg:py-24 px-4 bg-gradient-to-br from-blue-700 to-blue-900 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl lg:text-4xl font-extrabold mb-6">Questions About Payment Options?</h2>
          <p className="text-xl text-blue-200 mb-10">
            Our billing staff at both the DeLand and Orange City locations are here to help.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:386-775-1792"
              data-testid="sliding-fee-call-oc-btn"
              className="inline-flex items-center justify-center gap-2 bg-white text-blue-700 px-8 py-4 rounded-xl font-bold text-lg shadow-lg"
            >
              <Phone size={22} />
              Orange City: (386) 775-1792
            </a>
            <a
              href="tel:386-738-1792"
              data-testid="sliding-fee-call-dl-btn"
              className="inline-flex items-center justify-center gap-2 bg-blue-800 border-2 border-white text-white px-8 py-4 rounded-xl font-bold text-lg"
            >
              <Phone size={22} />
              DeLand: (386) 738-1792
            </a>
          </div>
          <p className="mt-8 text-blue-300 text-sm">
            <Link to="/contact" className="underline hover:text-white transition-colors">Contact us online</Link> or visit either location in person.
          </p>
        </div>
      </section>
    </div>
  );
};

export default SlidingFee;
